"use client"

import { useEffect, useRef, useState } from "react"
import { mouseStore } from "../mouseStore"

export interface VelocityState {
  /** Smoothed cursor speed in px/s */
  speed: number
  /** Direction of travel in degrees (0 = right, 90 = down, -90 = up) */
  angle: number
  /** Horizontal velocity component in px/s */
  vx: number
  /** Vertical velocity component in px/s */
  vy: number
  /** True when speed exceeds idleThreshold */
  isMoving: boolean
}

/**
 * Returns smoothed cursor velocity updated every animation frame.
 * Uses an exponential moving average so values don't jitter on every mouse event.
 * The RAF loop self-suspends after ~3s of inactivity and wakes on the next move.
 *
 * @param idleThreshold - px/s below which `isMoving` becomes false (default 50)
 */
export function useVelocityCursor({ idleThreshold = 50 }: { idleThreshold?: number } = {}): VelocityState {
  const [state, setState] = useState<VelocityState>({ speed: 0, angle: 0, vx: 0, vy: 0, isMoving: false })

  const rawRef = useRef({ vx: 0, vy: 0, lastX: 0, lastY: 0, lastTime: 0 })
  const smoothRef = useRef({ vx: 0, vy: 0 })
  const rafIdRef = useRef(0)

  useEffect(() => {
    rawRef.current.lastTime = performance.now()

    const EMA = 0.12
    // Suspend after this many consecutive near-zero frames (~3s at 60fps)
    const IDLE_FRAMES = 180
    let consecutiveIdle = 0

    const tick = () => {
      const now = performance.now()
      if (now - rawRef.current.lastTime > 60) {
        rawRef.current.vx *= 0.8
        rawRef.current.vy *= 0.8
      }

      const s = smoothRef.current
      s.vx += (rawRef.current.vx - s.vx) * EMA
      s.vy += (rawRef.current.vy - s.vy) * EMA

      const speed = Math.sqrt(s.vx * s.vx + s.vy * s.vy)
      const angle = speed > 1 ? Math.atan2(s.vy, s.vx) * (180 / Math.PI) : 0

      setState(prev => {
        const isMoving = speed > idleThreshold
        if (Math.abs(prev.speed - speed) < 1 && prev.isMoving === isMoving) return prev
        return { speed: Math.round(speed), angle: Math.round(angle), vx: Math.round(s.vx), vy: Math.round(s.vy), isMoving }
      })

      if (speed < 1) {
        consecutiveIdle++
        if (consecutiveIdle >= IDLE_FRAMES) {
          // Velocity fully decayed — suspend until next mouse movement
          rafIdRef.current = 0
          return
        }
      } else {
        consecutiveIdle = 0
      }

      rafIdRef.current = requestAnimationFrame(tick)
    }

    const unsub = mouseStore.subscribe((x, y) => {
      const now = performance.now()
      const dt = Math.max((now - rawRef.current.lastTime) / 1000, 0.001)
      rawRef.current.vx = (x - rawRef.current.lastX) / dt
      rawRef.current.vy = (y - rawRef.current.lastY) / dt
      rawRef.current.lastX = x
      rawRef.current.lastY = y
      rawRef.current.lastTime = now

      // Wake the RAF if it suspended
      if (rafIdRef.current === 0) {
        consecutiveIdle = 0
        rafIdRef.current = requestAnimationFrame(tick)
      }
    })

    rafIdRef.current = requestAnimationFrame(tick)

    return () => {
      unsub()
      if (rafIdRef.current !== 0) cancelAnimationFrame(rafIdRef.current)
    }
  }, [idleThreshold])

  return state
}
